import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";


import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  UserIcon,
  ArrowLeftIcon,
  SearchIcon,
  FileTextIcon,
  PlusIcon,
  TrashIcon,
  Loader2Icon,
  AlertCircleIcon,
  CheckIcon,
} from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import { Agent } from "../../model_gen/agent_base";
import { ApplicationAction } from "../../model_gen/application";
import { AgentService } from "../../services/AgentService";
import moment from "moment";

const AgentIntentManagement: React.FC = () => {
  const { apiKey } = usePermissions();
  const { applicationId, agentId } = useParams<{ applicationId: string; agentId: string }>();
  const navigate = useNavigate();
  
  const [agent, setAgent] = useState<Agent | null>(null);
  const [assignedIntents, setAssignedIntents] = useState<ApplicationAction[]>([]);
  const [orphanedIntents, setOrphanedIntents] = useState<ApplicationAction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [assignLoading, setAssignLoading] = useState<Record<number, boolean>>({});
  const [removeDialog, setRemoveDialog] = useState<ApplicationAction | null>(null);

  useEffect(() => {
    if (agentId && applicationId && apiKey) {
      loadData();
    }
  }, [agentId, applicationId, apiKey]);

  const loadData = async () => {
    if (!agentId || !applicationId || !apiKey) return;
    
    setIsLoading(true);
    try {
      // Load agent details
      AgentService.getAgent(parseInt(agentId), () => {}, apiKey).subscribe(
        (response: any) => {
          const agentData = response.agent || response;
          setAgent(agentData);
        },
        (error: any) => {
          console.error("Error loading agent:", error);
        }
      );

      // Note: Intent management has been removed from agents
      // Agents no longer have direct intent assignments
      setAssignedIntents([]);
      setOrphanedIntents([]);
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading data:", error);
      setIsLoading(false);
    }
  };

  const handleAssignIntent = (intent: ApplicationAction) => {
    // Intent assignment to agents has been removed
    console.log("Intent assignment is no longer supported");
  };

  const handleRemoveIntent = (intent: ApplicationAction) => {
    // Intent removal from agents has been removed
    console.log("Intent removal is no longer supported");
  };

  const filteredOrphanedIntents = orphanedIntents.filter(intent =>
    intent.action_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAssignedIntents = assignedIntents.filter(intent =>
    intent.action_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!agentId || !applicationId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-lg text-muted-foreground">Invalid agent or application ID</p>
          <Button onClick={() => navigate("/applications")}>
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Applications
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/application/${applicationId}/agents`)}
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </Button>
            <h1 className="text-3xl font-gc-demi-bold text-foreground">Intent Management</h1>
          </div>
          {agent && (
            <p className="text-muted-foreground">
              Managing intents for <span className="font-gc-med">{agent.agent_name}</span>
            </p>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search intents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Content */}
      {!isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Assigned Intents */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-maitai-green" />
                Assigned Intents ({filteredAssignedIntents.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredAssignedIntents.length === 0 ? (
                <div className="text-center py-8">
                  <FileTextIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchTerm ? "No matching assigned intents" : "No intents assigned yet"}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredAssignedIntents.map((intent) => (
                    <div
                      key={intent.id}
                      className="flex items-center justify-between p-3 border border-border rounded-lg"
                    >
                      <div className="space-y-1">
                        <p className="font-gc-med text-foreground">{intent.action_type}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>Created {moment(intent.date_created).format("MMM D, YYYY")}</span>
                          {intent.agent_name && (
                            <Badge variant="secondary" className="text-xs">
                              {intent.agent_name}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <AlertDialog
                        open={removeDialog?.id === intent.id}
                        onOpenChange={(open) => setRemoveDialog(open ? intent : null)}
                      >
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <TrashIcon className="w-4 h-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove Intent</AlertDialogTitle>
                            <AlertDialogDescription>
                              Remove "{intent.action_type}" from this agent? The intent will become unassigned.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRemoveIntent(intent)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Orphaned Intents */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircleIcon className="w-5 h-5 text-orange-500" />
                Unassigned Intents ({filteredOrphanedIntents.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredOrphanedIntents.length === 0 ? (
                <div className="text-center py-8">
                  <CheckIcon className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchTerm ? "No matching unassigned intents" : "All intents are assigned!"}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredOrphanedIntents.map((intent) => (
                    <div
                      key={intent.id}
                      className="flex items-center justify-between p-3 border border-border rounded-lg"
                    >
                      <div className="space-y-1">
                        <p className="font-gc-med text-foreground">{intent.action_type}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>Created {moment(intent.date_created).format("MMM D, YYYY")}</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAssignIntent(intent)}
                        disabled={assignLoading[intent.id]}
                      >
                        {assignLoading[intent.id] ? (
                          <Loader2Icon className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <PlusIcon className="w-4 h-4 mr-2" />
                            Assign
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AgentIntentManagement; 